import React, { useState, useEffect, useRef } from 'react';
import {
  Badge,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Box,
  Divider,
  Button,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsActive as NotificationsActiveIcon,
  MarkEmailRead as MarkEmailReadIcon,
  Visibility as VisibilityIcon,
  Error as ErrorIcon,
  ShoppingCart as ShoppingCartIcon,
  CheckCircle as CheckCircleIcon,
  LocalShipping as LocalShippingIcon,
  Receipt as ReceiptIcon,
  PersonAdd as PersonAddIcon,
  Campaign as CampaignIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { notificationService, NotificationSummary } from '../../services/notificationService';
import { useTranslation } from 'react-i18next';

interface NotificationBellProps {
  refreshInterval?: number; // in seconds, default 30
}

const NotificationBell: React.FC<NotificationBellProps> = ({ 
  refreshInterval = 30 
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [summary, setSummary] = useState<NotificationSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const open = Boolean(anchorEl);
  const id = open ? 'notification-popover' : undefined;

  const fetchSummary = async () => {
    try {
      setError(null);
      const data = await notificationService.getNotificationSummary();
      setSummary(data);
    } catch (err) {
      console.error('Error fetching notification summary:', err);
      setError(t('errors.notificationsLoadFailed'));
    }
  };

  useEffect(() => {
    // Initial fetch
    fetchSummary();

    // Set up polling
    intervalRef.current = setInterval(fetchSummary, refreshInterval * 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [refreshInterval]);

  const handleBellClick = async (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    
    if (!summary) {
      setLoading(true);
      await fetchSummary();
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notification: any) => {
    try {
      // Mark as clicked
      const result = await notificationService.markAsClicked(notification.id);
      
      // Close popover
      handleClose();
      
      // Navigate to action URL if available
      if (result.action_url) {
        const isExternal = result.action_type === 'external'
          || result.action_url.startsWith('http://')
          || result.action_url.startsWith('https://');
        if (isExternal) {
          window.open(result.action_url, '_blank', 'noopener,noreferrer');
        } else {
          navigate(result.action_url);
        }
      }
      
      // Refresh summary
      await fetchSummary();
    } catch (err) {
      console.error('Error handling notification click:', err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationService.markAllAsRead();
      await fetchSummary();
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const handleViewAll = () => {
    handleClose();
    navigate('/notifications');
  };

  const getBellIcon = () => {
    if (summary?.has_urgent || summary?.has_high_priority) {
      return <NotificationsActiveIcon sx={{ color: '#f44336' }} />;
    }
    return <NotificationsIcon />;
  };

  const getBadgeColor = () => {
    if (summary?.has_urgent) return 'error';
    if (summary?.has_high_priority) return 'warning';
    return 'primary';
  };

  const formatTimeAgo = (dateString?: string): string => {
    return notificationService.getTimeAgo(dateString);
  };

  const getNotificationIcon = (notification: any) => {
    const iconName = notification.icon || 'notifications';
    const iconColor = notification.icon_color || '#2196f3';
    
    // Return proper Material-UI icon based on icon name
    const iconStyle = { 
      fontSize: '20px', 
      color: iconColor
    };
    
    switch (iconName) {
      case 'shopping_cart': 
        return <ShoppingCartIcon sx={iconStyle} />;
      case 'check_circle': 
        return <CheckCircleIcon sx={iconStyle} />;
      case 'local_shipping': 
        return <LocalShippingIcon sx={iconStyle} />;
      case 'receipt': 
        return <ReceiptIcon sx={iconStyle} />;
      case 'person_add': 
        return <PersonAddIcon sx={iconStyle} />;
      case 'campaign': 
        return <CampaignIcon sx={iconStyle} />;
      default: 
        return <NotificationsIcon sx={iconStyle} />;
    }
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleBellClick}
        aria-describedby={id}
        sx={{ 
          mr: 2,
          animation: summary?.has_urgent ? 'pulse 2s infinite' : 'none',
          '@keyframes pulse': {
            '0%': { transform: 'scale(1)' },
            '50%': { transform: 'scale(1.1)' },
            '100%': { transform: 'scale(1)' }
          }
        }}
      >
        <Badge 
          badgeContent={summary?.unread_count || 0} 
          color={getBadgeColor()}
          max={99}
        >
          {getBellIcon()}
        </Badge>
      </IconButton>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: { 
            width: 420, 
            maxHeight: 520,
            mt: 1,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            borderRadius: 2
          }
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Notifikasi
            </Typography>
            {summary && summary.unread_count > 0 && (
              <Button 
                size="small" 
                onClick={handleMarkAllRead}
                startIcon={<MarkEmailReadIcon />}
                sx={{ minWidth: 'auto' }}
              >
                Tandai Semua Dibaca
              </Button>
            )}
          </Box>
          {summary && (
            <Typography variant="body2" color="text.secondary">
              {summary.unread_count} notifikasi belum dibaca
            </Typography>
          )}
        </Box>

        <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {error && (
            <Box sx={{ p: 2 }}>
              <Alert severity="error" icon={<ErrorIcon />}>
                {error}
              </Alert>
            </Box>
          )}

          {summary && !loading && !error && (
            <>
              {summary.recent_notifications.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Tidak ada notifikasi baru
                  </Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {summary.recent_notifications.map((notification, index) => (
                    <React.Fragment key={notification.id}>
                      <ListItem
                        onClick={() => handleNotificationClick(notification)}
                        sx={{
                          backgroundColor: notification.is_read ? 'transparent' : 'rgba(25, 118, 210, 0.04)',
                          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                          py: 1.5,
                          px: 2,
                          cursor: 'pointer',
                          alignItems: 'flex-start'
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                          {getNotificationIcon(notification)}
                        </ListItemIcon>
                        
                        <ListItemText
                          sx={{ mr: 1 }}
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                              <Typography 
                                variant="subtitle2" 
                                sx={{ 
                                  fontWeight: notification.is_read ? 400 : 600,
                                  fontSize: '0.875rem',
                                  lineHeight: 1.3,
                                  flex: 1,
                                  wordBreak: 'break-word'
                                }}
                              >
                                {notification.title}
                              </Typography>
                              {notification.priority !== 'normal' && (
                                <Chip
                                  label={notification.priority.toUpperCase()}
                                  size="small"
                                  color={
                                    notification.priority === 'urgent' ? 'error' :
                                    notification.priority === 'high' ? 'warning' : 'default'
                                  }
                                  sx={{ 
                                    height: 16, 
                                    fontSize: '0.6rem',
                                    minWidth: 'auto',
                                    flexShrink: 0
                                  }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 0.5 }}>
                              {notification.summary && notification.summary.trim() && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ 
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    fontSize: '0.75rem',
                                    lineHeight: 1.2,
                                    mb: 0.5,
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {notification.summary.trim()}
                                </Typography>
                              )}
                              <Typography 
                                variant="caption" 
                                color="text.secondary"
                                sx={{ 
                                  fontSize: '0.65rem',
                                  display: 'block',
                                  mt: 0.25
                                }}
                              >
                                {formatTimeAgo(notification.created_at)}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < summary.recent_notifications.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </>
          )}
        </Box>

        <Divider />
        <Box sx={{ p: 1.5 }}>
          <Button
            fullWidth
            onClick={handleViewAll}
            startIcon={<VisibilityIcon />}
            sx={{ 
              justifyContent: 'center',
              textTransform: 'none',
              fontSize: '0.875rem'
            }}
          >
            Lihat Semua Notifikasi
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default NotificationBell;